import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { SbsStatusType } from "@platform/plan-grid-core";

/** The derived flag held in the difference layer, apart from the main cell state. */
const DIFFERENCE_STATUS: SbsStatusType = "difference";

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

/** Metadata for a single SBS cell (row + field). */
export interface SbsCellMeta {
  statuses: SbsStatusType[];
  comment?: string;
  /**
   * Authoritative state of the underlying value when no display value is
   * available. "missing" renders as a hyphen; "null" renders as `[ ]`.
   * Ignored when the value can be formatted normally.
   */
  valueState?: "missing" | "null";
  /**
   * For a "difference" cell: the quoted value the flag was raised at, which a
   * dismissal stores so the flag can return when the value changes.
   */
  differenceValue?: string;
}

/** Cell state map: rowKey → fieldKey → SbsCellMeta. */
export type SbsCellStateMap = Record<string, Record<string, SbsCellMeta>>;

export interface SbsCellStateContextValue {
  /**
   * Retrieve metadata for a specific cell (rowKey + fieldKey). `network` picks
   * the network column of a plan, which carries its own difference flag.
   */
  getCellMeta: (
    rowKey: string,
    fieldKey: string,
    network?: string,
  ) => SbsCellMeta | undefined;

  /**
   * Toggle a flag on a specific cell. Adds if absent, removes if present. The
   * derived "difference" flag is not toggled here: it changes only through
   * `setDifferenceMap` and `dismissDifference`.
   */
  toggleFlag: (rowKey: string, fieldKey: string, flag: SbsStatusType) => void;

  /**
   * Remove all flags the cell holds in the main cell state. A derived
   * "difference" flag stays: a dismissal clears it.
   */
  clearFlags: (rowKey: string, fieldKey: string) => void;

  /** Bulk-set the entire cell state map (e.g., from AI extraction). */
  setCellStateMap: (map: SbsCellStateMap) => void;

  /**
   * Replace the "different than requested" flags. Held apart from
   * `setCellStateMap` so an empty difference map (every flag dismissed) always
   * applies, and a refresh of one source never wipes the other.
   */
  setDifferenceMap: (map: SbsCellStateMap) => void;

  /**
   * Clear the "different than requested" flag on a cell. The host decides how
   * the dismissal is stored; the provider only routes the request to it.
   */
  dismissDifference: (
    rowKey: string,
    fieldKey: string,
    network?: string,
  ) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SbsCellStateContext = createContext<SbsCellStateContextValue | null>(
  null,
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface SbsCellStateProviderProps {
  /** Optional initial state (e.g., pre-computed difference flags). */
  initialState?: SbsCellStateMap;
  /**
   * Persists an analyst's dismissal of a cell's difference flag, with the
   * quoted value the flag was raised at.
   */
  onDismissDifference?: (
    rowKey: string,
    fieldKey: string,
    differenceValue: string | undefined,
  ) => void;
  children: React.ReactNode;
}

export const SbsCellStateProvider = ({
  initialState,
  onDismissDifference,
  children,
}: SbsCellStateProviderProps): React.JSX.Element => {
  const [cellStateMap, setCellStateMap] = useState<SbsCellStateMap>(
    initialState ?? {},
  );

  const [differenceMap, setDifferenceMap] = useState<SbsCellStateMap>({});

  const getCellMeta = useCallback(
    (
      rowKey: string,
      fieldKey: string,
      network?: string,
    ): SbsCellMeta | undefined => {
      const meta = cellStateMap[rowKey]?.[fieldKey];
      // Difference flags are keyed by column field, so a network column's flag
      // sits under `planId.network`. A cell with no network split is flagged
      // whole, under `planId`, and shows on every network column.
      const rowDifferences = differenceMap[rowKey];
      const difference =
        (network ? rowDifferences?.[`${fieldKey}.${network}`] : undefined) ??
        rowDifferences?.[fieldKey];
      if (!difference) return meta;

      return {
        ...meta,
        statuses: [...(meta?.statuses ?? []), ...difference.statuses],
        differenceValue: difference.differenceValue,
      };
    },
    [cellStateMap, differenceMap],
  );

  const toggleFlag = useCallback(
    (rowKey: string, fieldKey: string, flag: SbsStatusType): void => {
      if (flag === DIFFERENCE_STATUS) return;

      setCellStateMap((prev) => {
        const existing = prev[rowKey]?.[fieldKey];
        const currentFlags = existing?.statuses ?? [];
        const hasFlag = currentFlags.includes(flag);

        const nextFlags = hasFlag
          ? currentFlags.filter((f) => f !== flag)
          : [...currentFlags, flag];

        return {
          ...prev,
          [rowKey]: {
            ...prev[rowKey],
            [fieldKey]: {
              ...existing,
              statuses: nextFlags,
            },
          },
        };
      });
    },
    [],
  );

  const clearFlags = useCallback((rowKey: string, fieldKey: string): void => {
    setCellStateMap((prev) => {
      const existing = prev[rowKey]?.[fieldKey];
      if (!existing) return prev;

      return {
        ...prev,
        [rowKey]: {
          ...prev[rowKey],
          [fieldKey]: { ...existing, statuses: [] },
        },
      };
    });
  }, []);

  const value = useMemo<SbsCellStateContextValue>(
    () => ({
      getCellMeta,
      toggleFlag,
      clearFlags,
      setCellStateMap,
      setDifferenceMap,
      dismissDifference: (rowKey, fieldKey, network) =>
        onDismissDifference?.(
          rowKey,
          fieldKey,
          getCellMeta(rowKey, fieldKey, network)?.differenceValue,
        ),
    }),
    [
      getCellMeta,
      toggleFlag,
      clearFlags,
      setCellStateMap,
      setDifferenceMap,
      onDismissDifference,
    ],
  );

  return (
    <SbsCellStateContext.Provider value={value}>
      {children}
    </SbsCellStateContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useSbsCellState = (): SbsCellStateContextValue => {
  const ctx = useContext(SbsCellStateContext);
  if (!ctx) {
    throw new Error("useSbsCellState must be used within SbsCellStateProvider");
  }
  return ctx;
};
